import { useEffect, useRef } from 'react';

import { buildDots, DOT_FIELD, DOT_SIZE, dotLook, dotMask, dotRadiusScale, dotScale, GUST_LIFE, GUST_MAX, paintDots, type DotLook, type Gust } from './dotFieldModel';
import styles from './Layout.module.css';

const FRAME_MS = 1000 / 30;
/** A moving mouse touches the dots again after this many px (at full size)… */
const GUST_STEP = 36;
/** …but not more often than this, in seconds. */
const GUST_GAP = 0.05;

function dotColor(): string {
  return getComputedStyle(document.documentElement).getPropertyValue('--dot').trim() || '#a6c1f0';
}


/**
 * Canvas with the design's dot grid behind the page, anchored top-right like
 * the XD design and shown on the right. Dots brighten and fade on their own
 * cycles, and brighten a little more where the pointer passes — a moving
 * mouse or a tap — unless the viewer prefers reduced motion. The footer sits
 * above it on a solid background.
 */
export function DotField(): React.JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;

    const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // the pixel ratio changes with the browser's zoom: read again on every fit
    let scale = Math.min(2, window.devicePixelRatio || 1);
    let width = 0;
    let height = 0;
    let radius = DOT_FIELD.radius;
    let scaleDots = 1;
    let look: DotLook = dotLook(window.innerWidth);
    let gusts: Gust[] = [];
    let lastGust: Gust | null = null;
    let dots = buildDots(DOT_FIELD, 2026, DOT_FIELD.width, DOT_FIELD.height, 1, look);
    let color = dotColor();
    let frame = 0;
    let lastPaint = 0;
    let lastColorCheck = 0;
    const started = performance.now();

    const seconds = (now: number): number => (now - started) / 1000;

    const paintStill = (): void => {
      paintDots(ctx, dots, { radius, color, seconds: null, width, height });
    };

    const paintNow = (): void => {
      if (reduceMotion) paintStill();
      else paintDots(ctx, dots, { radius, color, seconds: seconds(performance.now()), width, height, motion: look, gusts, scale: scaleDots });
    };

    // The canvas covers the whole viewport; it is rebuilt when the window changes size or the zoom changes.
    const fit = (): void => {
      const rect = canvas.getBoundingClientRect();
      const nextWidth = Math.round(rect.width) || window.innerWidth || DOT_FIELD.width;
      const nextHeight = Math.round(rect.height) || window.innerHeight || DOT_FIELD.height;
      const nextScale = Math.min(2, window.devicePixelRatio || 1);
      if (nextWidth === width && nextHeight === height && nextScale === scale) return;
      scale = nextScale;
      width = nextWidth;
      height = nextHeight;
      scaleDots = dotScale(width);
      look = dotLook(width);
      radius = DOT_FIELD.radius * dotRadiusScale(width) * DOT_SIZE;
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      dots = buildDots(DOT_FIELD, 2026, width, height, scaleDots, look, dotMask(width));
      // resizing the canvas clears it: paint again at once, so no blank or stretched frame shows in between
      paintNow();
    };
    fit();

    const tick = (now: number): void => {
      frame = window.requestAnimationFrame(tick);
      if (now - lastPaint < FRAME_MS) return;
      lastPaint = now;
      if (now - lastColorCheck > 1000) {
        lastColorCheck = now;
        color = dotColor();
      }
      const at = seconds(now);
      gusts = gusts.filter((gust) => at - gust.start < GUST_LIFE);
      paintDots(ctx, dots, { radius, color, seconds: at, width, height, motion: look, gusts, scale: scaleDots });
    };

    // The pointer lights the dots: a moving mouse leaves light touches along its way, a tap one where it lands.
    const addGust = (gust: Gust): void => {
      gusts = [...gusts.slice(-(GUST_MAX - 1)), gust];
      lastGust = gust;
    };
    const onPointerMove = (event: PointerEvent): void => {
      if (event.pointerType === 'touch') return;
      const at = seconds(performance.now());
      const last = lastGust;
      if (last && (at - last.start < GUST_GAP || Math.hypot(event.clientX - last.x, event.clientY - last.y) < GUST_STEP * scaleDots)) return;
      addGust({ x: event.clientX, y: event.clientY, start: at });
    };
    const onPointerDown = (event: PointerEvent): void => {
      addGust({ x: event.clientX, y: event.clientY, start: seconds(performance.now()) });
    };

    const start = (): void => {
      if (frame === 0) frame = window.requestAnimationFrame(tick);
    };
    const stop = (): void => {
      window.cancelAnimationFrame(frame);
      frame = 0;
    };
    const onVisibility = (): void => {
      if (document.hidden) stop();
      else start();
    };

    const observer = new MutationObserver(() => {
      color = dotColor();
      if (reduceMotion) paintStill();
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-system'] });
    const resizer = typeof ResizeObserver === 'function' ? new ResizeObserver(fit) : null;
    resizer?.observe(canvas);
    window.addEventListener('resize', fit);

    if (!reduceMotion) {
      start();
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointerdown', onPointerDown, { passive: true });
    }
    return () => {
      stop();
      observer.disconnect();
      resizer?.disconnect();
      window.removeEventListener('resize', fit);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, []);

  return <canvas ref={ref} className={styles.dots} aria-hidden="true" data-testid="dot-field" />;
}
